/**
 * Shared wallet-adapter transaction sender with compute-budget / priority-fee
 * instructions baked in. Every launch and swap goes through here so a busy
 * Solana mainnet doesn't silently drop our txs.
 */

import { ComputeBudgetProgram, PublicKey } from "@solana/web3.js";

// Same CB defaults used by sendTx() — factored so sendTxBundle reuses.
const CB_PROGRAM_ID = ComputeBudgetProgram.programId.toBase58();
const stripCb = (tx) => {
  tx.instructions = tx.instructions.filter(
    (ix) => ix.programId.toBase58() !== CB_PROGRAM_ID
  );
};
const prependCb = (tx, cuLimit, cuPrice) => {
  tx.instructions.unshift(
    ComputeBudgetProgram.setComputeUnitLimit({ units: cuLimit }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: cuPrice })
  );
};

const DEFAULT_CU_LIMIT = 400_000;
// 500k microlamports/CU. Higher than the previous 200k because we kept
// hitting blockhash-expired errors during congestion: at 200k the tx was
// being skipped by leaders for higher-priority blocks. 500k keeps us in
// the top ~5% of priority fees on Solana mainnet at almost any moment.
// Total tip per tx: ~0.0002 SOL (~$0.04). Cheap insurance.
// Now used as the FLOOR for dynamic priority fees — we only ever pay
// MORE than this if the network is hot enough that 500k won't land.
const DEFAULT_CU_PRICE = 500_000;
// Hard cap so a once-in-a-month congestion spike can't drain a creator's
// wallet on priority fees. 5M microlamports/CU × 400k CU = ~0.002 SOL
// (~$0.40 max). Real-world top-percentile fees rarely exceed this even
// in the worst minutes of mainnet congestion.
const PRIORITY_FEE_CAP_MICRO = 5_000_000;
const PRIORITY_ESTIMATE_TIMEOUT_MS = 500;

/**
 * Ask Helius for recent priority fees on the accounts THIS tx will touch,
 * take the 75th percentile, and bound by [floor, cap]. Returns the floor
 * if the RPC call fails or times out — so worst case we're identical to
 * the pre-dynamic behavior, never worse.
 *
 * Only fires once per launch (bundle uses union of writable accounts).
 * ~100-200ms latency on success, ~500ms on timeout (fallback path).
 */
async function estimatePriorityFee(connection, txs) {
  try {
    const writable = new Set();
    for (const tx of Array.isArray(txs) ? txs : [txs]) {
      for (const ix of tx.instructions) {
        for (const meta of ix.keys) {
          if (meta.isWritable) writable.add(meta.pubkey.toBase58());
        }
      }
    }
    // RPC method caps at 128 accounts. We take an arbitrary subset if more.
    const accounts = Array.from(writable).slice(0, 128).map((s) => new PublicKey(s));
    const fees = await Promise.race([
      connection.getRecentPrioritizationFees({ lockedWritableAccounts: accounts }),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("estimate-timeout")), PRIORITY_ESTIMATE_TIMEOUT_MS)
      ),
    ]);
    const nonZero = fees
      .map((f) => f.prioritizationFee)
      .filter((f) => typeof f === "number" && f > 0)
      .sort((a, b) => a - b);
    if (nonZero.length === 0) return DEFAULT_CU_PRICE;
    const p75 = nonZero[Math.floor(nonZero.length * 0.75)];
    return Math.min(Math.max(p75, DEFAULT_CU_PRICE), PRIORITY_FEE_CAP_MICRO);
  } catch {
    return DEFAULT_CU_PRICE;
  }
}

/**
 * Robust confirmation: poll signature status with re-broadcast every few
 * seconds until either it confirms OR we exceed the blockhash window.
 * Even if the connection's confirmTransaction times out (a common Helius
 * issue under load), we do one last on-chain `getSignatureStatuses` check
 * before declaring failure — because the tx may have actually landed.
 *
 * Returns { sig, err } where `err` is the on-chain error if the tx
 * reverted, or null if it succeeded. Throws only when the tx truly
 * never landed on-chain.
 */
async function confirmWithRebroadcast(connection, sig, signed, blockhash, lastValidBlockHeight) {
  const POLL_INTERVAL_MS = 1500;
  const REBROADCAST_EVERY_MS = 6000;
  let lastRebroadcast = Date.now();

  while (true) {
    // Did the tx land?
    const status = await connection.getSignatureStatuses([sig]);
    const s = status.value[0];
    if (s) {
      if (s.confirmationStatus === "confirmed" || s.confirmationStatus === "finalized") {
        return { sig, err: s.err ?? null };
      }
      // "processed" — keep polling
    }

    // Are we past blockhash validity?
    let blockHeight;
    try {
      blockHeight = await connection.getBlockHeight("confirmed");
    } catch {
      blockHeight = null;
    }
    if (blockHeight !== null && blockHeight > lastValidBlockHeight) {
      // One last check — the tx might have landed and just not propagated.
      const final = await connection.getSignatureStatuses([sig], { searchTransactionHistory: true });
      const f = final.value[0];
      if (f && (f.confirmationStatus === "confirmed" || f.confirmationStatus === "finalized")) {
        return { sig, err: f.err ?? null };
      }
      throw new Error(`Tx ${sig} expired before landing (blockhash window exceeded)`);
    }

    // Periodically re-broadcast — leader handoffs sometimes drop the tx.
    if (Date.now() - lastRebroadcast > REBROADCAST_EVERY_MS) {
      try {
        await connection.sendRawTransaction(signed.serialize(), {
          skipPreflight: true,
          maxRetries: 0,
        });
      } catch { /* best-effort re-broadcast */ }
      lastRebroadcast = Date.now();
    }

    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
}

/**
 * Send a legacy Transaction via the connected wallet adapter. Prepends
 * ComputeBudget instructions so our txs land during congestion.
 *
 * @param {Connection} connection
 * @param {object}     wallet         - wallet-adapter wallet (publicKey + signTransaction)
 * @param {Transaction} tx            - the transaction to sign/send
 * @param {Array}      [extraSigners] - additional keypairs to partial-sign
 * @param {object}     [opts]
 * @param {number}     [opts.cuLimit] - compute unit limit (default 400k)
 * @param {number}     [opts.cuPrice] - microlamports per CU (default 50k)
 */
export async function sendTx(connection, wallet, tx, extraSigners = [], opts = {}) {
  const cuLimit = opts.cuLimit ?? DEFAULT_CU_LIMIT;
  // Dynamic priority fee — query recent fees on this tx's writable accounts,
  // bounded by [DEFAULT_CU_PRICE, PRIORITY_FEE_CAP_MICRO]. Caller can still
  // override via opts.cuPrice for special cases. Adds ~100-200ms latency
  // but saves seconds during network congestion.
  const cuPrice = opts.cuPrice ?? (await estimatePriorityFee(connection, tx));

  // Bulletproof ComputeBudget normalization: strip ANY existing CB instructions
  // (regardless of which program/variant/order) then prepend ours. This makes
  // duplicate-instruction errors impossible even if the SDK added its own
  // CB ixs using a different PublicKey reference or unusual ordering.
  const CB_PROGRAM_ID = ComputeBudgetProgram.programId.toBase58();
  tx.instructions = tx.instructions.filter(
    (ix) => ix.programId.toBase58() !== CB_PROGRAM_ID
  );
  tx.instructions.unshift(
    ComputeBudgetProgram.setComputeUnitLimit({ units: cuLimit }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: cuPrice })
  );

  // `confirmed` gives a ~10s fresher blockhash than `finalized` — more of
  // the 60s validity window to spend on user signing + RPC propagation.
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");
  tx.recentBlockhash = blockhash;
  tx.lastValidBlockHeight = lastValidBlockHeight;
  tx.feePayer = wallet.publicKey;
  if (extraSigners.length) tx.partialSign(...extraSigners);
  const signed = await wallet.signTransaction(tx);
  const sig = await connection.sendRawTransaction(signed.serialize(), {
    skipPreflight: false,
    maxRetries: 5,
  });
  // Use our own confirm loop with re-broadcast + post-expire signature
  // check, instead of connection.confirmTransaction. Helius times out
  // confirmation pretty aggressively even when the tx actually lands;
  // the bare confirmTransaction path was throwing BlockheightExceeded
  // for txs that were already confirmed on chain.
  const { err: chainErr } = await confirmWithRebroadcast(
    connection, sig, signed, blockhash, lastValidBlockHeight
  );
  if (chainErr) {
    let logs = [];
    try {
      const parsed = await connection.getTransaction(sig, {
        commitment: "confirmed",
        maxSupportedTransactionVersion: 0,
      });
      logs = parsed?.meta?.logMessages ?? [];
    } catch {
      /* best-effort */
    }
    const errStr = JSON.stringify(chainErr);
    const logTail = logs.slice(-6).join(" | ");
    throw new Error(
      `Tx ${sig} reverted on-chain: ${errStr}${logTail ? ` · logs: ${logTail}` : ""}`
    );
  }
  return sig;
}

/**
 * Atomic bundle: sign multiple legacy txs in ONE wallet popup, then send
 * them sequentially. We use this so the launch's fee-split tx can't be
 * abandoned after the pool-creation tx — user signs both or neither.
 *
 * Each entry in `items` is `{ tx, extraSigners?, cuLimit?, cuPrice? }`.
 * All txs share the same blockhash (valid for ~2 min), so a dropped send
 * can be retried without re-signing.
 *
 * Optional `onProgress(i, phase, sig)` fires as each tx moves through
 * `broadcasting → broadcasted → confirmed`. Used by the launch flow to
 * update the stage label between individual bundle items.
 */
export async function sendTxBundle(connection, wallet, items, onProgress) {
  if (!wallet?.signAllTransactions) {
    throw new Error("Wallet doesn't support signAllTransactions");
  }
  // `confirmed` gives a ~10s fresher blockhash than `finalized` — more of
  // the 60s validity window to spend on user signing + RPC propagation.
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");

  // Dynamic priority fee — single estimate based on the union of writable
  // accounts across the whole bundle. Each item can still override via its
  // own cuPrice. Adds ~100-200ms once for the whole bundle, not per-tx.
  const dynamicPrice = await estimatePriorityFee(
    connection,
    items.map((i) => i.tx)
  );

  // Prep every tx: strip/prepend CB, set blockhash, partial-sign extras.
  for (const item of items) {
    const { tx, extraSigners = [], cuLimit = DEFAULT_CU_LIMIT, cuPrice = dynamicPrice } = item;
    stripCb(tx);
    prependCb(tx, cuLimit, cuPrice);
    tx.recentBlockhash = blockhash;
    tx.lastValidBlockHeight = lastValidBlockHeight;
    tx.feePayer = wallet.publicKey;
    if (extraSigners.length) tx.partialSign(...extraSigners);
  }

  const signed = await wallet.signAllTransactions(items.map((i) => i.tx));

  // Per-tx result so callers can treat later txs as best-effort (the launch
  // flow needs the pool-create to succeed, but split-fees can fail gracefully).
  const results = [];
  for (let i = 0; i < signed.length; i++) {
    const s = signed[i];
    const optional = items[i]?.optional === true;
    try {
      onProgress?.(i, "broadcasting");
      // skipPreflight on subsequent txs — first-tx state (pool created) may
      // not yet be visible to preflight RPC so it'd falsely reject.
      const sig = await connection.sendRawTransaction(s.serialize(), {
        skipPreflight: i > 0,
        maxRetries: 5,
      });
      onProgress?.(i, "broadcasted", sig);
      // Robust confirm: poll + re-broadcast + post-expire signature check
      // so a transient Helius confirmation timeout doesn't throw on a tx
      // that actually landed.
      const { err: chainErr } = await confirmWithRebroadcast(
        connection, sig, s, blockhash, lastValidBlockHeight
      );
      if (chainErr) {
        let logs = [];
        try {
          const parsed = await connection.getTransaction(sig, {
            commitment: "confirmed",
            maxSupportedTransactionVersion: 0,
          });
          logs = parsed?.meta?.logMessages ?? [];
        } catch {
          /* best-effort */
        }
        const errStr = JSON.stringify(chainErr);
        const logTail = logs.slice(-8).join(" | ");
        const err = new Error(
          `Tx ${sig} reverted on-chain: ${errStr}${logTail ? ` · logs: ${logTail}` : ""}`
        );
        if (optional) {
          console.warn("[sendTxBundle] optional tx failed:", err.message);
          results.push({ sig: null, err });
          continue;
        }
        throw err;
      }
      onProgress?.(i, "confirmed", sig);
      results.push({ sig, err: null });
    } catch (err) {
      if (optional) {
        console.warn("[sendTxBundle] optional tx threw:", err?.message || err);
        results.push({ sig: null, err });
        continue;
      }
      throw err;
    }
  }
  return results;
}
